import type { ReactNode } from 'react'
import type { HeroEngine } from '@/lib/three/hero-engine'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowDown, Smartphone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { supportsWebGL } from '@/lib/webgl'
import { onAdapted } from '@/lib/adaptation'
import { registerSkyPointer } from '@/lib/sky-pointer'
import { useReducedMotion } from '@/lib/preferences'
import { FadeLink } from '@/components/generals/fade-link'
import { SoundToggle } from '@/components/generals/toggles'
import { Media } from '@/components/generals/media'
import { AscentStills } from '@/components/landing/ascent-stills'
import { OBSERVATORY, formatCoordinate } from '@/constants/observatory'

import {
    ASCENT_SCREENS,
    MILESTONES,
    distanceAt,
    fadeWindow,
    phaseFor,
    readDistance
} from '@/lib/ascent-timeline'

const RULER_TICKS = Array.from({ length: 22 }, (_, index) => index)
const RULER_UNITS = [
    { label: 'km', at: 3 / 21.2 },
    { label: 'AU', at: (Math.log10(1e7) + 3) / 21.2 },
    { label: 'light-min', at: (Math.log10(1.8e9) + 3) / 21.2 },
    { label: 'light-years', at: (Math.log10(9.46e11) + 3) / 21.2 },
    { label: 'thousand ly', at: (Math.log10(9.46e15) + 3) / 21.2 }
]

/**
 * The home page opens inside the dome. The slit parts, the real sky appears,
 * and scrolling lifts you out through the slit and up to the edge of the galaxy.
 */
export const DomeAscent = (): ReactNode => {
    const reduced = useReducedMotion()
    const sectionRef = useRef<HTMLElement | null>(null)
    const stickyRef = useRef<HTMLDivElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const labelRef = useRef<HTMLDivElement | null>(null)
    const labelNameRef = useRef<HTMLParagraphElement | null>(null)
    const labelStoryRef = useRef<HTMLParagraphElement | null>(null)
    const markerRef = useRef<HTMLDivElement | null>(null)
    const heroTextRef = useRef<HTMLDivElement | null>(null)
    const hintRef = useRef<HTMLDivElement | null>(null)
    const rulerRef = useRef<HTMLDivElement | null>(null)
    const rulerMarkerRef = useRef<HTMLDivElement | null>(null)
    const readingValueRef = useRef<HTMLSpanElement | null>(null)
    const readingUnitRef = useRef<HTMLSpanElement | null>(null)
    const milestoneRef = useRef<HTMLDivElement | null>(null)
    const milestoneDistanceRef = useRef<HTMLParagraphElement | null>(null)
    const milestoneLineRef = useRef<HTMLParagraphElement | null>(null)
    const engineRef = useRef<HeroEngine | null>(null)
    const reducedRef = useRef(reduced)
    const lastMilestone = useRef(-1)
    const lastReading = useRef('')
    const [failed, setFailed] = useState(false)
    const [ready, setReady] = useState(false)
    const [touch, setTouch] = useState(false)
    const [tilt, setTilt] = useState<'off' | 'on' | 'unavailable'>('off')

    reducedRef.current = reduced

    const updateOverlays = useCallback((sectionProgress: number) => {
        const { rise, ascent } = phaseFor(reducedRef.current ? 0 : sectionProgress)
        const heroFade = Math.max(0, 1 - Math.max(rise * 1.6, (sectionProgress - 0.004) * 60))

        if (heroTextRef.current) {
            heroTextRef.current.style.opacity = String(heroFade)
            heroTextRef.current.style.transform = `translate3d(0, ${(1 - heroFade) * -24}px, 0)`
            heroTextRef.current.style.pointerEvents = heroFade > 0.4 ? 'auto' : 'none'
        }

        if (hintRef.current) {
            hintRef.current.style.opacity = String(heroFade)
            hintRef.current.style.visibility = heroFade > 0.01 ? 'visible' : 'hidden'
        }

        const a = Math.max(0, ascent)
        const inAscent = ascent > 0 && !reducedRef.current
        const rulerOpacity = inAscent ? Math.min(1, ascent * 40) * (1 - Math.max(0, (a - 0.955) * 22)) : 0

        if (rulerRef.current) rulerRef.current.style.opacity = String(Math.max(0, rulerOpacity))

        if (inAscent) {
            const reading = readDistance(distanceAt(a))
            const key = `${reading.value} ${reading.unit}`

            if (key !== lastReading.current) {
                lastReading.current = key

                if (readingValueRef.current) readingValueRef.current.textContent = reading.value
                if (readingUnitRef.current) readingUnitRef.current.textContent = reading.unit
            }

            if (rulerMarkerRef.current) rulerMarkerRef.current.style.transform = `translate3d(0, ${(1 - reading.ruler) * 100}%, 0)`
        }

        let active = -1
        let strength = 0

        if (inAscent) {
            MILESTONES.forEach((milestone, index) => {
                const value = fadeWindow(a, milestone.start, milestone.end)

                if (value > strength) {
                    strength = value
                    active = index
                }
            })
        }

        if (active >= 0 && active !== lastMilestone.current) {
            lastMilestone.current = active

            if (milestoneDistanceRef.current) milestoneDistanceRef.current.textContent = MILESTONES[active].distance
            if (milestoneLineRef.current) milestoneLineRef.current.textContent = MILESTONES[active].line
        }

        if (milestoneRef.current) {
            milestoneRef.current.style.opacity = String(strength)
            milestoneRef.current.style.transform = `translate3d(0, ${(1 - strength) * 14}px, 0)`
        }
    }, [])

    useEffect(() => {
        setTouch(window.matchMedia('(hover: none), (pointer: coarse)').matches)
    }, [])

    useEffect(() => {
        const canvas = canvasRef.current
        const sticky = stickyRef.current
        const section = sectionRef.current

        if (!canvas || !sticky || !section) return

        let cancelled = false
        let unregister = () => undefined as void
        let stopAdapted = () => undefined as void
        let observer: IntersectionObserver | null = null
        let resizeObserver: ResizeObserver | null = null

        const updateScroll = () => {
            const engine = engineRef.current

            if (!engine) return

            if (reducedRef.current) {
                engine.setScroll(0)
                return
            }

            const rect = section.getBoundingClientRect()
            const total = section.offsetHeight - window.innerHeight
            const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0

            engine.setScroll(progress)
        }

        const start = async () => {
            if (!supportsWebGL()) {
                setFailed(true)
                return
            }

            try {
                const { createHeroEngine } = await import('@/lib/three/hero-engine')
                const mobile = window.matchMedia('(max-width: 768px)').matches || window.matchMedia('(pointer: coarse)').matches
                const engine = await createHeroEngine({
                    canvas,
                    host: sticky,
                    label: labelRef.current ?? undefined,
                    labelName: labelNameRef.current ?? undefined,
                    labelStory: labelStoryRef.current ?? undefined,
                    marker: markerRef.current ?? undefined,
                    mobile,
                    interactive: true,
                    onProgress: updateOverlays
                })

                if (cancelled) {
                    engine.dispose()
                    return
                }

                engineRef.current = engine
                engine.setReducedMotion(reducedRef.current)
                updateScroll()
                setReady(true)

                unregister = registerSkyPointer(sticky, (x, y) => engineRef.current?.skyCoordinates(x, y) ?? null)
                stopAdapted = onAdapted(() => engineRef.current?.open())

                observer = new IntersectionObserver(([entry]) => {
                    engineRef.current?.setActive(entry.isIntersecting)
                }, { rootMargin: '120px 0px' })
                observer.observe(section)

                resizeObserver = new ResizeObserver(() => {
                    engineRef.current?.resize()
                    updateScroll()
                })
                resizeObserver.observe(sticky)
            }
            catch (error) {
                console.error('The sky could not be drawn here:', error)
                if (!cancelled) setFailed(true)
            }
        }

        void start()
        window.addEventListener('scroll', updateScroll, { passive: true })

        return () => {
            cancelled = true
            window.removeEventListener('scroll', updateScroll)
            unregister()
            stopAdapted()
            observer?.disconnect()
            resizeObserver?.disconnect()
            engineRef.current?.dispose()
            engineRef.current = null
        }
    }, [updateOverlays])

    useEffect(() => {
        engineRef.current?.setReducedMotion(reduced)
        engineRef.current?.setScroll(0)
        lastMilestone.current = -1
        lastReading.current = ''
        window.dispatchEvent(new Event('scroll'))
    }, [reduced])

    const requestTilt = async () => {
        const engine = engineRef.current

        if (!engine) return

        const granted = await engine.enableTilt()

        setTilt(granted ? 'on' : 'unavailable')
    }

    const sectionHeight = reduced || failed ? '100svh' : `calc(100svh * ${1 + ASCENT_SCREENS})`

    return (
        <>
            <section
                ref={sectionRef}
                id='hero'
                aria-labelledby='hero-title'
                className='relative'
                style={{ height: sectionHeight }}
            >
                <div
                    ref={stickyRef}
                    data-sky
                    data-cursor='drag'
                    className='sticky top-0 h-[100svh] w-full touch-pan-y select-none overflow-hidden bg-black'
                >
                    {failed ? (
                        <Media
                            src='/images/dome-milky-way.jpg'
                            alt='The CosmicLens dome on its hill beneath the Milky Way'
                            priority
                            className='absolute inset-0'
                        />
                    ) : (
                        <div className='media absolute inset-0'>
                            <canvas
                                ref={canvasRef}
                                className={cn('block h-full w-full transition-opacity duration-[2000ms]', ready ? 'opacity-100' : 'opacity-0')}
                                aria-label='The night sky seen through the opening slit of the observatory dome. Drag to look around.'
                                role='img'
                            />
                        </div>
                    )}

                    <div aria-hidden className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_120%,rgba(0,0,0,0.55),transparent_60%)]' />

                    <div
                        ref={labelRef}
                        aria-hidden
                        className='pointer-events-none absolute left-0 top-0 w-[17rem] opacity-0 transition-opacity duration-500 will-change-transform'
                    >
                        <p className='label-mono text-brass'>
                            Constellation
                        </p>
                        <p ref={labelNameRef} className='mt-1 font-heading text-3xl font-light text-star' />
                        <p ref={labelStoryRef} className='mt-2 text-sm leading-relaxed text-star-2' />
                    </div>

                    <div
                        ref={markerRef}
                        aria-hidden
                        className='pointer-events-none absolute left-0 top-0 opacity-0'
                    >
                        <div className='-translate-x-1/2 -translate-y-1/2'>
                            <span className='block size-5 rounded-full border border-brass' />
                        </div>
                        <p className='label-mono absolute left-4 top-3 whitespace-nowrap text-brass'>
                            You are here
                        </p>
                    </div>

                    <div ref={heroTextRef} className='absolute inset-x-0 bottom-0 pb-24 pt-40 md:pb-28'>
                        <div className='container-page'>
                            <p className='label-mono text-star-2'>
                                <span className='text-brass'>
                                    CosmicLens Observatory
                                </span>
                                <span className='mx-3 text-star-3'>
                                    /
                                </span>
                                {`${formatCoordinate(OBSERVATORY.latitude, 'N', 'S')}  ${formatCoordinate(OBSERVATORY.longitude, 'E', 'W')}`}
                            </p>
                            <h1 id='hero-title' className='display-serif mt-5 max-w-4xl text-[3.4rem] leading-[0.95] text-star sm:text-7xl md:text-8xl lg:text-[8.5rem]'>
                                Come and look up.
                            </h1>
                            <div className='mt-9 flex flex-wrap items-center gap-x-8 gap-y-4'>
                                <a
                                    href='#sky-tonight'
                                    className='group inline-flex items-center gap-3 text-star'
                                >
                                    <span className='h-px w-8 bg-brass transition-all duration-500 group-hover:w-12' />
                                    <span className='label-mono'>
                                        Tonight&apos;s sky
                                    </span>
                                </a>
                                <FadeLink
                                    href='/shows'
                                    className='label-mono inline-flex h-11 items-center rounded-full border border-brass/70 px-6 text-star transition-colors duration-500 hover:bg-brass hover:text-ink'
                                >
                                    Book a show
                                </FadeLink>
                            </div>
                            {!reduced && (
                                <button
                                    type='button'
                                    onClick={() => window.scrollBy({ top: window.innerHeight * 0.9, behavior: 'smooth' })}
                                    className='mt-8 inline-flex items-center gap-2 text-star-2 md:hidden'
                                >
                                    <ArrowDown aria-hidden className='hint-drift size-4 text-brass' strokeWidth={1.25} />
                                    <span className='label-mono'>
                                        The long way up
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>

                    <div ref={hintRef} className='pointer-events-none absolute inset-x-0 bottom-0'>
                        <div className='container-page flex items-end justify-between pb-6'>
                            <div className='pointer-events-auto'>
                                <SoundToggle />
                            </div>

                            {!reduced && (
                                <button
                                    type='button'
                                    onClick={() => window.scrollBy({ top: window.innerHeight * 0.9, behavior: 'smooth' })}
                                    className='pointer-events-auto absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-star-2 transition-colors hover:text-star md:flex'
                                >
                                    <span className='label-mono'>
                                        The long way up
                                    </span>
                                    <ArrowDown aria-hidden className='hint-drift size-4 text-brass' strokeWidth={1.25} />
                                </button>
                            )}

                            <div className='pointer-events-auto flex items-center gap-3'>
                                {touch && tilt === 'off' && ready && (
                                    <button
                                        type='button'
                                        onClick={() => void requestTilt()}
                                        className='label-mono inline-flex h-9 items-center gap-2 rounded-full border border-hair-2 px-4 text-star-2'
                                    >
                                        <Smartphone aria-hidden className='size-3.5' strokeWidth={1.25} />
                                        Tilt to look
                                    </button>
                                )}
                                {!failed && (
                                    <p className='label-mono hidden text-star-3 sm:block'>
                                        {touch ? 'Drag to look around' : 'Drag to look around · hover the stars'}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {!reduced && (
                        <>
                            <div
                                ref={milestoneRef}
                                aria-live='polite'
                                className='pointer-events-none absolute bottom-[14vh] left-0 max-w-xl px-5 opacity-0 sm:px-8 lg:left-[6vw] lg:px-0'
                            >
                                <p ref={milestoneDistanceRef} className='label-mono text-brass' />
                                <p ref={milestoneLineRef} className='mt-3 font-heading text-[1.65rem] font-light leading-snug text-star md:text-[2.1rem]' />
                            </div>

                            <div
                                ref={rulerRef}
                                aria-hidden
                                className='pointer-events-none absolute bottom-[12vh] right-5 top-[16vh] w-24 opacity-0 sm:right-8 lg:right-12'
                            >
                                <div className='absolute bottom-0 right-0 top-0 w-px bg-hair-2' />
                                {RULER_TICKS.map(tick => (
                                    <span
                                        key={tick}
                                        className={cn('absolute right-0 h-px bg-star-3', tick % 3 === 0 ? 'w-3' : 'w-1.5 opacity-60')}
                                        style={{ top: `${(1 - tick / 21.2) * 100}%` }}
                                    />
                                ))}
                                {RULER_UNITS.map(unit => (
                                    <span
                                        key={unit.label}
                                        className='label-mono absolute right-5 -translate-y-1/2 whitespace-nowrap text-[0.6rem] text-star-3'
                                        style={{ top: `${(1 - unit.at) * 100}%` }}
                                    >
                                        {unit.label}
                                    </span>
                                ))}
                                <div ref={rulerMarkerRef} className='absolute inset-0 will-change-transform'>
                                    <div className='absolute right-0 top-0 flex -translate-y-1/2 items-center'>
                                        <div className='mr-3 text-right'>
                                            <span ref={readingValueRef} className='data-mono block text-lg text-star' />
                                            <span ref={readingUnitRef} className='label-mono block whitespace-nowrap text-[0.6rem] text-brass' />
                                        </div>
                                        <span className='block h-px w-6 bg-brass' />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>

            {reduced && <AscentStills />}
        </>
    )
}
